import { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetWebhookDeliveriesDTO from "../../../../models/DTO/IGetWebhookDeliveriesDTO";
import IOnGetWebhookDeliveriesDTO from "../../../../models/DTO/IOnGetWebhookDeliveriesDTO";
import IOnRedeliverWebhookDTO from "../../../../models/DTO/IOnRedeliverWebhookDTO";
import IRedeliverWebhookDTO from "../../../../models/DTO/IRedeliverWebhookDTO";
import INotification from "../../../../models/INotification";
import IWebhookDeliverySummary from "../../../../models/IWebhookDeliverySummary";
import IWebhookSummary from "../../../../models/IWebhookSummary";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import WebhookDeliveryStatusEnum from "../../../../models/WebhookDeliveryStatusEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { deliveryStatusLabelKey, webhookEventLabelKey } from "../Webhooks/webhookEvents";
import "./WebhookDeliveries.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";

type Props = {
    webhook: IWebhookSummary;
    cancelFn: () => void;
};

const PAGE_SIZE = 25;

const STATUSES = [
    WebhookDeliveryStatusEnum.PENDING,
    WebhookDeliveryStatusEnum.PROCESSING,
    WebhookDeliveryStatusEnum.SUCCEEDED,
    WebhookDeliveryStatusEnum.FAILED,
    WebhookDeliveryStatusEnum.CANCELLED,
];

/** The delivery log of one endpoint: status, attempts, the endpoint's last answer, the envelope, and redeliver. */
export const WebhookDeliveries = (props: Props) => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [deliveries, setDeliveries] = useState<IWebhookDeliverySummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [status, setStatus] = useState<WebhookDeliveryStatusEnum | "">("");
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [redelivering, setRedelivering] = useState<string | null>(null);

    // The request whose reply we are waiting for. Replies are matched to it by requestId, so a reply to a
    // superseded page or filter cannot land on the list (the socket handlers below are registered once).
    const requestSeq = useRef(0);
    const latestRequest = useRef<{ id: string; status: WebhookDeliveryStatusEnum | "" } | null>(null);

    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    const load = (nextPage: number, nextStatus: WebhookDeliveryStatusEnum | "") => {
        const id = String(++requestSeq.current);

        latestRequest.current = { id, status: nextStatus };

        const payload: IGetWebhookDeliveriesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            webhookGuid: props.webhook.guid,
            status: nextStatus || undefined,
            limit: PAGE_SIZE,
            offset: nextPage,
            includePayload: true,
            requestId: id,
        };

        setLoading(true);
        socket.emit(EndpointsEnum.WebhooksGetDeliveries, payload);
    };

    const onGetDeliveries = (data: IOnGetWebhookDeliveriesDTO) => {
        // A reply to an earlier page or filter: the list has moved on without it.
        if (data.requestId && latestRequest.current && data.requestId !== latestRequest.current.id) {
            return;
        }

        setLoading(false);
        setTotal(data.total || 0);
        setDeliveries((previous) => (data.offset > 0 ? [...previous, ...data.deliveries] : data.deliveries));
    };

    const onRedeliver = (data: IOnRedeliverWebhookDTO) => {
        setRedelivering(null);

        const notification: INotification = {
            message: "successfully_queued_redelivery",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        const filter = latestRequest.current?.status || "";

        // The row no longer belongs to the filtered list (failed -> pending): reload the first page so the
        // list and its count describe the same set.
        if (filter && data.delivery.status !== filter) {
            setPage(0);
            setExpanded(null);
            load(0, filter);

            return;
        }

        setDeliveries((previous) =>
            previous.map((delivery) => (delivery.guid === data.delivery.guid ? { ...delivery, ...data.delivery } : delivery))
        );
    };

    /** A rejected list or redeliver request: drop the pending state so the panel is usable again. */
    const onNotify = (payload: { type?: string; message?: string }) => {
        if (
            payload?.type !== "error" ||
            !/^(WEBHOOK_|ERROR_(GET_WEBHOOK_DELIVERIES|REDELIVER_WEBHOOK)$)/.test(String(payload.message || ""))
        ) {
            return;
        }

        setLoading(false);
        setRedelivering(null);
    };

    useEffect(() => {
        socket.on(EndpointsEnum.WebhooksOnGetDeliveries, onGetDeliveries);
        socket.on(EndpointsEnum.WebhooksOnRedeliver, onRedeliver);
        socket.on(EndpointsEnum.Notify, onNotify);
        load(0, "");

        return () => {
            socket.off(EndpointsEnum.WebhooksOnGetDeliveries, onGetDeliveries);
            socket.off(EndpointsEnum.WebhooksOnRedeliver, onRedeliver);
            socket.off(EndpointsEnum.Notify, onNotify);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeStatus = (next: WebhookDeliveryStatusEnum | "") => {
        setStatus(next);
        setPage(0);
        setExpanded(null);
        load(0, next);
    };

    const loadMore = () => {
        const next = page + 1;

        setPage(next);
        load(next, status);
    };

    const redeliver = (delivery: IWebhookDeliverySummary) => {
        if (redelivering) {
            return;
        }

        const payload: IRedeliverWebhookDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            deliveryGuid: delivery.guid,
        };

        setRedelivering(delivery.guid);
        socket.emit(EndpointsEnum.WebhooksRedeliver, payload);
    };

    const when = (value: string | null) => (value ? intl.formatDate(value, { dateStyle: "medium", timeStyle: "short" }) : "");

    return (
        <div className={"WebhookDeliveries p-2"} data-testid={"webhook-deliveries"}>
            <div className={"col-12"}>
                <div className="text-end">
                    <button className="btn btn-danger btn-sm" onClick={props.cancelFn} data-testid={"webhook-deliveries-close"}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle className={"mb-1"} leftIconName={"fad fa-list-timeline"} title={"webhook_deliveries"} />
                <div className={"WebhookDeliveries-endpoint"}>
                    <strong>{props.webhook.name}</strong>
                    <code>{props.webhook.url}</code>
                </div>
                <hr />
            </div>

            <div className={"WebhookDeliveries-bar"}>
                <select
                    className={"form-select form-select-sm"}
                    value={status}
                    aria-label={t("status")}
                    onChange={(e) => changeStatus(e.target.value as WebhookDeliveryStatusEnum | "")}
                    data-testid={"webhook-deliveries-status"}
                >
                    <option value={""}>{t("all_statuses")}</option>
                    {STATUSES.map((candidate) => (
                        <option key={candidate} value={candidate}>
                            {t(deliveryStatusLabelKey(candidate))}
                        </option>
                    ))}
                </select>
                <OwButton
                    centerIconClass={loading ? "fas fa-spinner fa-spin" : "fad fa-rotate-right"}
                    className={"btn-link text-muted btn-sm"}
                    ariaLabel={t("refresh")}
                    hoverTitle={t("refresh")}
                    fn={() => changeStatus(status)}
                    disableWidthOnClick={true}
                    disableDblClickPrevention={true}
                    testId={"webhook-deliveries-refresh"}
                />
            </div>

            {!loading && !deliveries.length ? (
                <div className={"WebhookDeliveries-empty"} data-testid={"webhook-deliveries-empty"}>
                    <div className={"WebhookDeliveries-empty-title"}>
                        <OwLabel title={"no_deliveries_yet"} noBottomMargin={true} />
                    </div>
                    <div className={"WebhookDeliveries-help"}>
                        <OwLabel title={"help_text_no_deliveries"} noBottomMargin={true} />
                    </div>
                </div>
            ) : null}

            <div className={"WebhookDeliveries-list"}>
                {deliveries.map((delivery) => {
                    const open = expanded === delivery.guid;
                    const canRedeliver =
                        delivery.status !== WebhookDeliveryStatusEnum.PENDING && delivery.status !== WebhookDeliveryStatusEnum.PROCESSING;

                    return (
                        <div
                            className={`WebhookDeliveries-row ${delivery.status}`}
                            key={delivery.guid}
                            data-testid={`delivery-${delivery.guid}`}
                        >
                            <button
                                type={"button"}
                                className={"WebhookDeliveries-summary"}
                                onClick={() => setExpanded(open ? null : delivery.guid)}
                                aria-expanded={open}
                                data-testid={`delivery-toggle-${delivery.guid}`}
                            >
                                <span className={`WebhookDeliveries-status ${delivery.status}`}>
                                    {t(deliveryStatusLabelKey(delivery.status))}
                                </span>
                                <span className={"WebhookDeliveries-event"}>
                                    {t(webhookEventLabelKey(delivery.event_type))}
                                    {delivery.is_test ? <span className={"WebhookDeliveries-tag"}>{t("test")}</span> : null}
                                </span>
                                <span className={"WebhookDeliveries-meta"}>
                                    {delivery.last_http_status ? `HTTP ${delivery.last_http_status}` : null}
                                    {` · ${t("attempts")} ${delivery.attempt_count}/${delivery.max_attempts}`}
                                    {` · ${when(delivery.last_attempt_at || delivery.created_at)}`}
                                </span>
                            </button>
                            {open ? (
                                <div className={"WebhookDeliveries-detail"} data-testid={`delivery-detail-${delivery.guid}`}>
                                    {delivery.next_attempt_at && delivery.status === WebhookDeliveryStatusEnum.PENDING ? (
                                        <div>{t("next_attempt", { date: when(delivery.next_attempt_at) })}</div>
                                    ) : null}
                                    {delivery.last_error ? (
                                        <div className={"text-danger"}>
                                            <strong>{t("last_error")}:</strong> {delivery.last_error}
                                        </div>
                                    ) : null}
                                    {delivery.last_duration_ms !== null && delivery.last_duration_ms !== undefined ? (
                                        <div className={"WebhookDeliveries-help"}>
                                            {t("delivery_duration", { ms: delivery.last_duration_ms })}
                                        </div>
                                    ) : null}
                                    {delivery.last_response_body ? (
                                        <>
                                            <strong>{t("response")}</strong>
                                            <pre>{delivery.last_response_body}</pre>
                                        </>
                                    ) : null}
                                    {delivery.payload ? (
                                        <>
                                            <strong>{t("view_payload")}</strong>
                                            <pre>{JSON.stringify(delivery.payload, null, 2)}</pre>
                                        </>
                                    ) : null}
                                    {canRedeliver ? (
                                        <OwButton
                                            title={"redeliver"}
                                            className={"btn-outline-secondary btn-sm"}
                                            leftIconClass={
                                                redelivering === delivery.guid ? "fas fa-spinner fa-spin" : "fad fa-rotate-right"
                                            }
                                            fn={() => redeliver(delivery)}
                                            disabled={!!redelivering}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`delivery-redeliver-${delivery.guid}`}
                                        />
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            {deliveries.length < total ? (
                <div className={"mt-2"}>
                    <OwButton
                        title={"load_more"}
                        className={"btn-link btn-sm ps-0"}
                        fn={loadMore}
                        active={loading}
                        disableOnActive={true}
                        disableWidthOnClick={true}
                        testId={"webhook-deliveries-more"}
                    />
                </div>
            ) : null}
        </div>
    );
};
